import * as React from 'react'
import type { ComponentType, JSX } from 'react'
import clsx from 'clsx'

import {
  ArrowDownIcon,
  ArrowUpIcon,
  BlueskyIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  MastodonIcon,
  PinterestIcon,
  ThreadsIcon,
  TiktokIcon,
  TwitterIcon,
  YoutubeIcon,
} from '@buffer-mono/popcorn'
import type { IconProps } from '@buffer-mono/popcorn'

import styles from './InsightsSnapshot.module.css'

export interface SummaryStat {
  label: string
  /** Pre-formatted value, e.g. "12.5k" or "23%". */
  value: string
  /** Optional pre-formatted difference, e.g. "12%" or "+12". */
  differenceLabel?: string
  differenceDirection?: 'up' | 'down' | null
}

export interface InsightsSnapshotProps {
  /** Seeds the avatar-fallback initial when no avatar URL is provided. */
  channelName?: string
  /** Raw service identifier (e.g. "instagram") — drives the icon overlay. */
  channelService?: string
  /** Avatar URL — caller is responsible for CORS-safe proxying. */
  channelAvatarUrl?: string
  /** Date range string (e.g. "May 2 - Jun 1, 2026"). */
  dateRangeLabel: string
  /** Optional prefix on the subtitle line (e.g. "5 channels"). */
  channelCountLabel?: string
  /** Caption rendered in each tile's bottom-right (e.g. "vs prev. month"). */
  comparisonLabel: string
  /** Up to 4 stats; missing slots render as empty tiles. */
  stats: readonly SummaryStat[]
  /**
   * When set, replaces the metric grid with this image (e.g. a generic graph
   * illustration for a no-data announcement).
   */
  placeholderImageUrl?: string
  /**
   * Compact variant for embedding in the launch announcement: drops the Buffer
   * wordmark footer and lets the card size to its content instead of the fixed
   * 1080×1080 square. Defaults to false so the shareable image is unaffected.
   */
  compact?: boolean
}

type IconComp = ComponentType<IconProps>

const CHANNEL_ICONS: Record<string, IconComp> = {
  bluesky: BlueskyIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GraphQL service id is snake_case
  instagram_login_api: InstagramIcon,
  linkedin: LinkedinIcon,
  mastodon: MastodonIcon,
  pinterest: PinterestIcon,
  threads: ThreadsIcon,
  tiktok: TiktokIcon,
  twitter: TwitterIcon,
  x: TwitterIcon,
  youtube: YoutubeIcon,
}

const CHANNEL_BRAND_COLORS: Record<string, string> = {
  bluesky: '#1285FE',
  facebook: '#1877F2',
  googlebusiness: '#95BF47',
  instagram: '#F00276',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GraphQL service id is snake_case
  instagram_login_api: '#F00276',
  linkedin: '#2867B2',
  mastodon: '#6364FF',
  pinterest: '#E60023',
  startPage: '#1F8B4F',
  threads: '#000000',
  tiktok: '#000000',
  twitter: '#000000',
  x: '#000000',
  youtube: '#FF0000',
}

const DEFAULT_BRAND_COLOR = '#1F8B4F'

// Buffer wordmark — inlined SVG (same path as the legacy canvas
// implementation) so the snapshot is self-contained.
function BufferLogo(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="39"
      viewBox="0 0 74 19"
      width="160"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        clipRule="evenodd"
        d="M0 4.23717L8.03416 0L16.1549 4.23717L8.03416 8.48914L0 4.23717ZM48.2324 6.29724V6.19638C48.2324 4.79863 49.0398 4.35193 50.3998 4.4528V1.90229C47.1416 1.70055 45.5975 3.35766 45.5975 6.19638V6.29724H44.1242V8.84775H45.5975V16.5713H48.2324V8.84775H49.7904H50.3998H51.2637V16.5713H53.8986V8.84775H56.066V6.29724H53.8986V6.19638C53.8986 4.79863 54.6918 4.35193 56.066 4.4528V1.90229C52.8079 1.70055 51.2637 3.35766 51.2637 6.19638V6.29724H50.3998H49.7904H48.2324ZM29.8166 9.09245C30.6524 8.41523 31.1623 7.44975 31.1623 6.21054C31.1623 3.80411 29.2358 2.1614 26.8134 2.1614H21.1896V16.5423H27.2667C29.7457 16.5423 31.7289 14.8419 31.7289 12.3779C31.7006 10.8937 30.964 9.75529 29.8166 9.09245ZM26.8276 4.8272C27.7342 4.8272 28.3575 5.50446 28.3575 6.41227C28.3575 7.32007 27.7058 7.99735 26.8276 7.99735H24.0085V4.8272H26.8276ZM27.2526 13.9053H24.0085V10.519H27.2526C28.2159 10.519 28.9099 11.2395 28.9099 12.2049C28.9099 13.1848 28.2159 13.9053 27.2526 13.9053ZM39.9451 6.28238V11.8301C39.9451 13.617 38.9819 14.3807 37.6928 14.3807C36.5029 14.3807 35.667 13.6601 35.667 12.2624V6.28238H33.0322V12.5939C33.0322 15.3317 34.7179 16.8447 36.8995 16.8447C38.2736 16.8447 39.336 16.3259 39.931 15.4037V16.5565H42.5658V6.28238H39.9451ZM66.9877 12.5076H59.2818C59.6643 13.8622 60.7691 14.4385 62.1433 14.4385C63.1773 14.4385 63.9991 14.0063 64.4379 13.4155L66.563 14.6403C65.614 16.038 64.084 16.845 62.1147 16.845C58.6867 16.845 56.5194 14.4818 56.5194 11.4125C56.5194 8.34328 58.7008 5.98006 61.9167 5.98006C64.934 5.98006 67.0873 8.38647 67.0873 11.4125C67.0873 11.816 67.0445 12.1618 66.9877 12.5076ZM61.9167 8.38647C60.4997 8.38647 59.5367 9.15023 59.2391 10.4615H64.4519C64.1262 8.97726 63.0214 8.38647 61.9167 8.38647ZM70.8977 8.05523V6.28286H68.2629V16.557H70.8977V11.6432C70.8977 9.48178 72.6258 8.86218 74 9.03509V6.08108C72.7113 6.08108 71.422 6.6575 70.8977 8.05523ZM8.03416 15.7599L2.55304 12.7337L0 14.1388L8.03416 18.57L16.1549 14.1388L13.573 12.7337L8.03416 15.7599ZM2.55304 7.95856L8.03416 10.7029L13.573 7.95856L16.1549 9.23926L8.03416 13.2643L0 9.23926L2.55304 7.95856Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

/**
 * 1080 × 1080 React component that renders the Insights Summary share
 * snapshot. Designed to be mounted offscreen and captured to a PNG via
 * `html-to-image` (see `captureSummaryAsBlob`) — using a real DOM means
 * every visual rule comes from CSS modules + Popcorn tokens, not from
 * hand-tuned canvas math.
 *
 * Lives in `components/` (not `pages/Insights/`) so it can also be loaded
 * from a Storybook story for visual review.
 */
export function InsightsSnapshot({
  channelName,
  channelService,
  channelAvatarUrl,
  dateRangeLabel,
  channelCountLabel,
  comparisonLabel,
  stats,
  placeholderImageUrl,
  compact = false,
}: InsightsSnapshotProps): JSX.Element {
  const ChannelIcon = channelService ? CHANNEL_ICONS[channelService] : undefined
  const brandColor = channelService
    ? (CHANNEL_BRAND_COLORS[channelService] ?? DEFAULT_BRAND_COLOR)
    : DEFAULT_BRAND_COLOR

  // Always render 4 tile slots so missing data still draws the grid chrome
  // (matches the canvas-era behaviour the composer expects).
  const slots = [
    { key: 'snapshot-stat-1', stat: stats[0] },
    { key: 'snapshot-stat-2', stat: stats[1] },
    { key: 'snapshot-stat-3', stat: stats[2] },
    { key: 'snapshot-stat-4', stat: stats[3] },
  ]

  // Drop the avatar slot entirely when we have nothing to render (e.g. a user
  // on the All Channels page with no profile photo). Showing a `?` placeholder
  // here looked worse than just hiding the slot, per design feedback.
  const avatar = channelAvatarUrl ? (
    <img
      alt=""
      className={styles.avatar}
      crossOrigin="anonymous"
      src={channelAvatarUrl}
    />
  ) : channelName ? (
    <div className={styles.avatarFallback}>
      {channelName.charAt(0).toUpperCase()}
    </div>
  ) : null

  const subtitle = channelCountLabel
    ? `${channelCountLabel} · ${dateRangeLabel}`
    : dateRangeLabel

  return (
    <div className={clsx(styles.root, compact && styles.compact)}>
      <header>
        {avatar ? (
          <div className={styles.avatarWrap}>
            {avatar}
            {ChannelIcon ? (
              <div
                className={styles.channelIcon}
                style={{ backgroundColor: brandColor }}
              >
                <ChannelIcon color="inverted" size={20} />
              </div>
            ) : null}
          </div>
        ) : null}
        <div>
          <h1 className={styles.title}>Insights Snapshot</h1>
          <p className={styles.dateRange}>{subtitle}</p>
        </div>
      </header>

      {placeholderImageUrl ? (
        <img alt="" className={styles.placeholder} src={placeholderImageUrl} />
      ) : (
        <div className={styles.grid}>
          {slots.map(({ key, stat }) => (
            <div key={key} className={styles.tile}>
              {stat ? (
                <>
                  <div className={styles.tileLabel}>
                    {stat.label.toUpperCase()}
                  </div>
                  <div className={styles.tileValue}>{stat.value}</div>
                  <div className={styles.tileBottom}>
                    <div className={styles.tileTrend}>
                      {stat.differenceLabel && stat.differenceDirection ? (
                        <>
                          {stat.differenceDirection === 'up' ? (
                            <ArrowUpIcon
                              className={styles.arrowUp}
                              color="inherit"
                              size={32}
                            />
                          ) : (
                            <ArrowDownIcon
                              className={styles.arrowDown}
                              color="inherit"
                              size={32}
                            />
                          )}
                          <span>{stat.differenceLabel}</span>
                        </>
                      ) : null}
                    </div>
                    <span className={styles.tileCompare}>
                      {comparisonLabel}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {compact ? null : (
        <footer className={styles.footer}>
          <span className={styles.bufferLogo}>
            <BufferLogo />
          </span>
        </footer>
      )}
    </div>
  )
}
